import { FormGroup } from '@angular/forms';
import { fixBarcode } from '@app/modules/kits/kits.utils';
import { sampleFieldConfiguration } from '@app/modules/service-data/components/sample/sample-dialog/sample-field-configuration';
import { sampleValidationGroups } from '@app/modules/service-data/components/sample/sample-dialog/sample-validation-configuration';
import { serviceDataFieldConfiguration } from '@app/modules/service-data/components/service-data/service-data-form/service-data-field-configuration';
import { serviceDataValidationGroups } from '@app/modules/service-data/components/service-data/service-data-form/service-data-validation-configuration';
import {
  CustomReportGenerationData,
  ExtendedProductInventoryItem,
  ValidationPair,
} from '@app/modules/service-data/types/service-data.types';
import { Product } from '@core/core.types';
import moment from 'moment';

export enum ValidatorsHandlerType {
  SERVICEDATA = 'serviceData',
  SAMPLE = 'sample',
}

export function handleValidators(
  products: Product[],
  group: FormGroup,
  root: string,
  type: ValidatorsHandlerType,
  ignore: string[] = []
) {
  clearValidators(group, root, ignore);
  setValidators(
    group,
    root,
    type,
    products
      .map((product) => product?.dataInputType)
      .filter((inputType) => !!inputType)
  );
}

// Clear all validators from group (recursively)
function clearValidators(
  group: FormGroup,
  controlName: string,
  ignore: string[] = []
) {
  if (ignore.includes(controlName)) {
    return;
  }
  const control = controlName ? group.get(controlName) : group;

  control.clearValidators();
  control.updateValueAndValidity();

  if (control instanceof FormGroup) {
    Object.keys(control.controls).forEach((key) => {
      clearValidators(group, controlName ? `${controlName}.${key}` : key);
    });
  }
}

// Set validators for selected products
function setValidators(
  group: FormGroup,
  controlName: string,
  type: ValidatorsHandlerType,
  inputTypes: string[]
) {
  inputTypes = [...new Set(inputTypes)];

  const validationGroups =
    type === ValidatorsHandlerType.SERVICEDATA
      ? serviceDataValidationGroups
      : sampleValidationGroups;
  for (const groupName of inputTypes) {
    if (!validationGroups[groupName]) {
      continue;
    }
    setValidatorsRecursively(group, controlName, validationGroups[groupName]);
  }
}

function setValidatorsRecursively(
  group: FormGroup,
  controlName: string,
  validationGroup: ValidationPair
) {
  const control = controlName ? group.get(controlName) : group;
  if (!control) {
    console.warn('Field doesnt exist ', controlName);
    return null;
  }
  if (validationGroup.validators) {
    control.addValidators(validationGroup.validators);
    control.updateValueAndValidity();
  }
  if (validationGroup.children) {
    for (const key of Object.keys(validationGroup.children)) {
      setValidatorsRecursively(
        group,
        controlName ? `${controlName}.${key}` : key,
        validationGroup.children[key]
      );
    }
  }
}

export function getFieldSet(inputTypes: string[], type: ValidatorsHandlerType) {
  let fieldSet = [];
  const validationConfiguration =
    type === ValidatorsHandlerType.SERVICEDATA
      ? serviceDataFieldConfiguration
      : sampleFieldConfiguration;
  for (const inputType of inputTypes) {
    fieldSet = [...fieldSet, ...(validationConfiguration[inputType] ?? [])];
  }

  return [...new Set(fieldSet)];
}

export const redirectToDhl = (dhlWaybill: string) => {
  window.open(
    `https://www.dhl.com/si-en/home/tracking/tracking-express.html?submit=1&tracking-id=${dhlWaybill.replace(
      / /g,
      ''
    )}`,
    '_blank'
  );
};

export const trimBarcode = (barcode: string) => {
  let bar = barcode;
  if (barcode && barcode.includes('-')) {
    bar = bar.split('-')[0];
  }

  if (barcode && barcode.includes('R')) {
    bar = bar.replace('R', '');
  }

  return fixBarcode(bar);
};

export const getDateOnly = (date: string): string => {
  if (!date) {
    return '';
  }

  const dateMoment = moment.utc(date);

  const utcMinutes = dateMoment.hours() * 60 + dateMoment.minutes();

  return dateMoment
    .startOf('day')
    .add(utcMinutes >= 12 * 60 ? 1 : 0, 'day')
    .format('YYYY-MM-DD');
};

export const getDateOnlyMoment = (date: string): moment.Moment => {
  const dateOnly = getDateOnly(date);

  if (!dateOnly) {
    return null;
  }

  const [year, month, day] = dateOnly.split('-').map((part) => Number(part));

  return moment({ year, month: month - 1, day });
};

export const getReportReportGenerationData = (
  data: ExtendedProductInventoryItem,
  approvedBy: string,
  partnerLanguageCode: string
): CustomReportGenerationData => ({
  Barcode: data.sample.kitId,
  PatientName: `${data.sample.serviceData.patientInformation.firstName} ${data.sample.serviceData.patientInformation.lastName}`,
  PatientDateOfBirth: getDateOnly(
    data.sample.serviceData.patientInformation.dateOfBirth
  ),
  GestationWeeks:
    data.sample.serviceData.serviceInformation.nipt?.currentPregnancyInformation
      ?.gestationAge?.week,
  GestationDays:
    data.sample.serviceData.serviceInformation.nipt?.currentPregnancyInformation
      ?.gestationAge?.day,
  DocumentId: data.sample.serviceData.patientInformation.documentNumber,
  DateOfVenipuncture: new Date(data.sample.dateOfVenipuncture).toISOString(),
  DateOfConfinement: data.sample.serviceData.serviceInformation.nipt
    ?.currentPregnancyInformation?.expectedConfinementDate
    ? new Date(
        data.sample.serviceData.serviceInformation.nipt?.currentPregnancyInformation?.expectedConfinementDate
      ).toISOString()
    : null,
  ServiceName: data.product.internalName,
  CountryCode: data.partner.address.countryIsoCode,
  LanguageCode: partnerLanguageCode,
  ApprovedBy: approvedBy,
  Gender:
    data.sample.serviceData.serviceInformation.nipt?.provideGenderInformation,
  IncidentalFindings:
    data.sample.serviceData.serviceInformation.nipt?.incidentalFindings,
  productInventoryItemId: data.id,
  testId: data.id,
  sampleExtractorId: data.sample.doctor.id,
  doctorId: data.sample.serviceData.doctor.id,
  SampleReceived: data.sample.kit.returned
    ? new Date(data.sample.kit.returned.timestamp).toISOString()
    : null,
  DoctorName: data.sample.serviceData.doctor.name,
});
